// src/components/admin/RefDictionaryPage.js
// Универсальная страница-справочник для админ-панели.
// Принимает endpoint, описание колонок и список полей формы — отдаёт
// готовую страницу со списком, поиском, модалкой создания/редактирования
// и удалением (одиночным или массовым).

import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { AdminShell } from './Sidebar';
import Modal, { ModalButton } from '../ui/Modal';
import {
  StatCard, RefreshButton, SearchField,
  TableCard, LoadingState, EmptyState, Pagination, BulkBar,
} from '../ui/CrmPage';

const sortIcon = (active, dir) => (
  <span className={`crmpage__sort ${active ? 'is-active' : ''}`} aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

function desc(a, b, getVal) {
  const av = getVal(a); const bv = getVal(b);
  if (bv == null && av != null) return -1;
  if (av == null && bv != null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
const getComparator = (order, getter) =>
  order === 'desc' ? (a, b) => desc(a, b, getter) : (a, b) => -desc(a, b, getter);
const stableSort = (arr, comp) =>
  arr.map((el, i) => [el, i]).sort((a, b) => { const o = comp(a[0], b[0]); return o || a[1] - b[1]; }).map((el) => el[0]);

/**
 * Props:
 *  - breadcrumbs, title, subtitle — для AdminShell
 *  - endpoint: 'v1/organizations' (без apiBase, без слеша спереди)
 *  - listMapper: (raw) => array — если бэк возвращает не массив, тут нормализуем
 *  - columns: [{ key, label, width?, align?, wrap?, render? }]
 *  - fields: [{ key, label, type? = 'text'|'textarea'|'number'|'select', required?, options?, placeholder?, hint? }]
 *  - emptyRecord: исходное состояние формы для «создать»
 *  - itemTitle: (row) => string — заголовок модалки при редактировании
 *  - canDelete?: boolean
 *  - searchKeys?: array<string> — поля по которым ищем по тексту
 *  - readOnly?: boolean — отключает кнопки добавления/удаления/редактирования
 *  - extraRefs?: { [name]: 'v1/organizations' } — подгружаемые справочники для select-полей
 */
export default function RefDictionaryPage({
  breadcrumbs, title, subtitle,
  endpoint,
  listMapper,
  columns,
  fields,
  emptyRecord = {},
  itemTitle = (row) => row?.name || row?.id || '',
  canDelete = true,
  searchKeys,
  readOnly = false,
  extraRefs,
}) {
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [rows, setRows] = useState([]);
  const [refs, setRefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [q, setQ] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const [selected, setSelected] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyRecord);
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  // Загружаем основной список + сторонние справочники для селектов
  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const reqs = [
          fetch(`${apiBase}${endpoint}`),
          ...Object.values(extraRefs || {}).map((ep) => fetch(`${apiBase}${ep}`).catch(() => null)),
        ];
        const [mainRes, ...extraResList] = await Promise.all(reqs);
        if (!mainRes.ok) {
          const t = await mainRes.text().catch(() => '');
          throw new Error(`HTTP ${mainRes.status} ${mainRes.statusText} ${t}`.trim());
        }
        const raw = await mainRes.json();
        const list = listMapper ? listMapper(raw) : (Array.isArray(raw) ? raw : []);
        if (abort) return;
        setRows(list);
        setSelected([]);

        const newRefs = {};
        const refKeys = Object.keys(extraRefs || {});
        for (let i = 0; i < refKeys.length; i += 1) {
          const r = extraResList[i];
          if (r?.ok) {
            const d = await r.json().catch(() => []);
            newRefs[refKeys[i]] = Array.isArray(d) ? d : [];
          } else {
            newRefs[refKeys[i]] = [];
          }
        }
        if (!abort) setRefs(newRefs);
      } catch (e) {
        if (!abort) setError(e.message || 'Не удалось загрузить справочник');
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, endpoint, reloadKey]);

  // Поиск
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    const keys = searchKeys?.length ? searchKeys : columns.map((c) => c.key);
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term)));
  }, [rows, q, columns, searchKeys]);

  // Сортировка
  const sortGetter = useMemo(() => (r) => {
    const v = r[orderBy];
    if (typeof v === 'number') return v;
    return String(v ?? '').toLowerCase();
  }, [orderBy]);
  const sorted = useMemo(
    () => stableSort(filtered, getComparator(order, sortGetter)),
    [filtered, order, sortGetter]
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const requestSort = (key) => {
    const isAsc = orderBy === key && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(key);
  };

  // Selection
  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const ids = paged.map((r) => r.id);
    const allSelected = ids.every((id) => selected.includes(id));
    setSelected(allSelected ? selected.filter((id) => !ids.includes(id)) : Array.from(new Set([...selected, ...ids])));
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Удалить выбранных (${selected.length})?`)) return;
    try {
      await Promise.all(selected.map((id) => fetch(`${apiBase}${endpoint}/${id}`, { method: 'DELETE' })));
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Не удалось удалить выбранные записи');
    }
  };

  // Modal CRUD
  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyRecord });
    setFormError('');
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    // забираем только поля из fields + id
    const initial = {};
    fields.forEach((f) => { initial[f.key] = row[f.key] ?? ''; });
    setForm(initial);
    setFormError('');
    setDialogOpen(true);
  };

  const onField = (key, value) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  const submitForm = async () => {
    setFormError('');
    for (const f of fields) {
      if (f.required && (form[f.key] == null || form[f.key] === '')) {
        setFormError(`Заполните поле «${f.label}»`);
        return;
      }
    }
    setFormSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.key];
        if (v === '') v = null;
        if (f.type === 'number' && v != null) v = Number(v);
        payload[f.key] = v;
      });
      const url = editingId == null
        ? `${apiBase}${endpoint}`
        : `${apiBase}${endpoint}/${editingId}`;
      const method = editingId == null ? 'POST' : 'PUT';
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        let msg = `HTTP ${r.status}`;
        try { const j = JSON.parse(t); msg = j.message || j.error || msg; } catch { msg = t || msg; }
        throw new Error(msg);
      }
      setDialogOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setFormError(e.message || 'Ошибка сохранения');
    } finally {
      setFormSaving(false);
    }
  };

  return (
    <AdminShell
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
      actions={!readOnly && (
        <button type="button" className="adminbtn adminbtn--primary" onClick={openCreate}>
          <AddIcon sx={{ fontSize: 18 }} />
          Добавить
        </button>
      )}
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          <section className="crmpage__stats crmpage__stats--compact">
            <StatCard label="Всего записей" value={rows.length} accent="neutral" />
            <StatCard label="В выборке"     value={filtered.length} accent="brand" />
            <RefreshButton onClick={() => setReloadKey((k) => k + 1)} />
          </section>

          <section className="crmpage__filters">
            <SearchField
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Поиск по списку…"
            />
          </section>

          {error && <div className="crmpage__alert crmpage__alert--error">{error}</div>}

          <TableCard>
            {loading ? (
              <LoadingState text="Загружаем…" />
            ) : paged.length === 0 ? (
              <EmptyState
                title="Ничего не найдено"
                text={q ? 'Очистите поиск.' : (readOnly ? 'В справочнике пока нет записей.' : 'Добавьте первую запись.')}
              />
            ) : (
              <>
                <div className="crmpage__table-wrap">
                  <table className="crmpage__table">
                    <colgroup>
                      {!readOnly && canDelete && <col style={{ width: 40 }} />}
                      {columns.map((c) => (
                        <col key={c.key} style={c.width ? { width: c.width } : undefined} />
                      ))}
                      {!readOnly && <col style={{ width: 64 }} />}
                    </colgroup>
                    <thead>
                      <tr>
                        {!readOnly && canDelete && (
                          <th className="crmpage__th-check">
                            <input
                              type="checkbox"
                              className="crmpage__check"
                              checked={paged.length > 0 && paged.every((r) => selected.includes(r.id))}
                              onChange={toggleSelectAll}
                              aria-label="Выделить все"
                            />
                          </th>
                        )}
                        {columns.map((c) => (
                          <th
                            key={c.key}
                            onClick={() => requestSort(c.key)}
                            style={{ cursor: 'pointer', textAlign: c.align || 'left' }}
                          >
                            {c.label} {sortIcon(orderBy === c.key, order)}
                          </th>
                        ))}
                        {!readOnly && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((row) => {
                        const isChecked = selected.includes(row.id);
                        return (
                          <tr
                            key={row.id}
                            className={`crmpage__row ${isChecked ? 'crmpage__row--selected' : ''}`}
                            onClick={!readOnly ? () => openEdit(row) : undefined}
                            style={readOnly ? { cursor: 'default' } : undefined}
                          >
                            {!readOnly && canDelete && (
                              <td className="crmpage__td-check" onClick={(e) => { e.stopPropagation(); toggleSelect(row.id); }}>
                                <input type="checkbox" className="crmpage__check" checked={isChecked} onChange={() => toggleSelect(row.id)} />
                              </td>
                            )}
                            {columns.map((c) => {
                              const value = row[c.key];
                              const content = c.render ? c.render(value, row, refs) : (value == null || value === '' ? '—' : String(value));
                              return (
                                <td
                                  key={c.key}
                                  className={`${c.wrap ? 'crmpage__td--wrap' : ''} ${c.muted ? 'crmpage__muted' : ''}`.trim()}
                                  style={c.align ? { textAlign: c.align } : undefined}
                                >
                                  {content}
                                </td>
                              );
                            })}
                            {!readOnly && (
                              <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="adminbtn adminbtn--icon"
                                  title="Редактировать"
                                  onClick={() => openEdit(row)}
                                ><EditIcon sx={{ fontSize: 18 }} /></button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </TableCard>

          {!readOnly && canDelete && (
            <BulkBar count={selected.length} onClear={() => setSelected([])}>
              <button type="button" className="crmpage__btn crmpage__btn--danger" onClick={handleDeleteSelected}>
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                Удалить выбранные
              </button>
            </BulkBar>
          )}
        </div>
      </div>

      <Modal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingId == null ? `Добавить · ${title}` : `Изменить · ${itemTitle(rows.find((r) => r.id === editingId)) || '—'}`}
        size="md"
        footer={
          <>
            <ModalButton variant="secondary" onClick={() => setDialogOpen(false)}>Отмена</ModalButton>
            <ModalButton variant="primary" onClick={submitForm} disabled={formSaving}>
              {formSaving ? 'Сохранение…' : 'Сохранить'}
            </ModalButton>
          </>
        }
      >
        {formError && <div className="crmpage__alert crmpage__alert--error" style={{ marginBottom: 12 }}>{formError}</div>}
        <div className="adminform__grid adminform__grid--full">
          {fields.map((f) => {
            const v = form[f.key] ?? '';
            const onChange = (e) => onField(f.key, e.target.value);
            return (
              <label key={f.key} className="adminform__field">
                <span className="adminform__label">
                  {f.label}{f.required && <span className="req">*</span>}
                </span>
                {f.type === 'textarea' ? (
                  <textarea className="adminform__textarea" value={v} onChange={onChange} rows={3} placeholder={f.placeholder || ''} />
                ) : f.type === 'select' ? (
                  <select className="adminform__select" value={v} onChange={onChange}>
                    <option value="">— не выбрано —</option>
                    {(f.options || (f.refsKey ? refs[f.refsKey] : []) || []).map((opt) => {
                      const id = opt.id ?? opt.value;
                      const name = opt.name ?? opt.label ?? String(id);
                      return <option key={id} value={id}>{name}</option>;
                    })}
                  </select>
                ) : (
                  <input
                    type={f.type === 'number' ? 'number' : 'text'}
                    className="adminform__input"
                    value={v}
                    onChange={onChange}
                    placeholder={f.placeholder || ''}
                  />
                )}
                {f.hint && <span className="adminform__hint">{f.hint}</span>}
              </label>
            );
          })}
        </div>
      </Modal>
    </AdminShell>
  );
}
